import type OpenAI from 'openai';
import type { ChatCompletionMessageParam } from 'openai/resources/chat/completions';
import { UserError } from '../../errors';
import type { ModelMetadata, ModelRequest } from '../../model';
import { itemsToMessages } from '../openai/chat-completions-converter';
import {
  OpenAIChatCompletionsModel,
  type OpenAIChatCompletionsModelOptions,
  type OpenAIChatCompletionsRequestData,
} from '../openai/chat-completions-model';

type KimiThinkingType = 'enabled' | 'disabled';

export class KimiChatCompletionsModel extends OpenAIChatCompletionsModel {
  constructor(client: OpenAI, model: string, metadata?: ModelMetadata) {
    super(client, model, metadata, {
      itemsToMessages: itemsToKimiMessages,
      prepareRequestData: prepareKimiRequestData,
    });
  }
}

function itemsToKimiMessages(
  items: ModelRequest['input'],
): ChatCompletionMessageParam[] {
  return itemsToMessages(items).map((message) => {
    if (message.role !== 'assistant') {
      return message;
    }

    const record = message as ChatCompletionMessageParam &
      Record<string, unknown>;
    if (typeof record.reasoning !== 'string') {
      return message;
    }

    const { reasoning, ...rest } = record;
    return {
      ...rest,
      reasoning_content:
        typeof record.reasoning_content === 'string'
          ? record.reasoning_content
          : reasoning,
    } as unknown as ChatCompletionMessageParam;
  });
}

const prepareKimiRequestData: NonNullable<
  OpenAIChatCompletionsModelOptions['prepareRequestData']
> = ({ requestData, model }) => {
  const prepared: OpenAIChatCompletionsRequestData = {
    ...requestData,
  };
  const normalizedModel = model.trim().toLowerCase();

  const maxTokens = prepared.max_tokens;
  delete prepared.max_tokens;
  if (maxTokens !== undefined && prepared.max_completion_tokens === undefined) {
    prepared.max_completion_tokens = maxTokens;
  }

  delete prepared.reasoning_effort;
  delete prepared.reasoning;
  removeTextVerbosity(prepared);

  if (
    isKimiConfigurableThinkingModel(normalizedModel) &&
    prepared.thinking === undefined
  ) {
    prepared.thinking = { type: 'enabled' };
  }

  const thinkingType = getKimiThinkingType(prepared.thinking);
  if (prepared.thinking !== undefined && thinkingType === undefined) {
    throw new UserError(
      'Kimi thinking must be an object with type "enabled" or "disabled".',
    );
  }

  const thinkingEnabled = isKimiThinkingEnabled(normalizedModel, thinkingType);
  if (isKimiFixedParameterModel(normalizedModel)) {
    assertFixedNumber(
      prepared,
      'temperature',
      thinkingEnabled ? 1 : 0.6,
      normalizedModel,
    );
    assertFixedNumber(prepared, 'top_p', 0.95, normalizedModel);
    assertFixedNumber(prepared, 'presence_penalty', 0, normalizedModel);
    assertFixedNumber(prepared, 'frequency_penalty', 0, normalizedModel);
  }

  if (thinkingEnabled && isRestrictedToolChoice(prepared.tool_choice)) {
    throw new UserError(
      'Kimi thinking mode only supports modelSettings.toolChoice values "auto" or "none".',
    );
  }
  if (thinkingEnabled) {
    ensureToolCallMessagesHaveReasoningContent(prepared.messages);
  }

  pruneUndefinedValues(prepared);
  return prepared;
};

function isKimiConfigurableThinkingModel(normalizedModel: string): boolean {
  return normalizedModel === 'kimi-k2.6' || normalizedModel === 'kimi-k2.5';
}

function isKimiFixedParameterModel(normalizedModel: string): boolean {
  return isKimiConfigurableThinkingModel(normalizedModel);
}

function isKimiThinkingEnabled(
  normalizedModel: string,
  thinkingType: KimiThinkingType | undefined,
): boolean {
  if (thinkingType === 'disabled') {
    return false;
  }
  if (thinkingType === 'enabled') {
    return true;
  }
  return (
    normalizedModel === 'kimi-k2-thinking' ||
    normalizedModel === 'kimi-k2-thinking-turbo'
  );
}

function getKimiThinkingType(value: unknown): KimiThinkingType | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  return value.type === 'enabled' || value.type === 'disabled'
    ? value.type
    : undefined;
}

function assertFixedNumber(
  requestData: OpenAIChatCompletionsRequestData,
  key: 'temperature' | 'top_p' | 'presence_penalty' | 'frequency_penalty',
  expected: number,
  model: string,
): void {
  const value = requestData[key];
  if (value === undefined || value === expected) {
    return;
  }
  throw new UserError(
    `Kimi model ${model} requires ${key} to be ${expected}. Omit it to use the official default.`,
  );
}

function isRestrictedToolChoice(toolChoice: unknown): boolean {
  return (
    toolChoice !== undefined && toolChoice !== 'auto' && toolChoice !== 'none'
  );
}

function ensureToolCallMessagesHaveReasoningContent(
  messages: OpenAIChatCompletionsRequestData['messages'],
): void {
  for (const message of messages) {
    if (message.role !== 'assistant') {
      continue;
    }
    const record = message as ChatCompletionMessageParam &
      Record<string, unknown>;
    if (!Array.isArray(record.tool_calls) || record.tool_calls.length === 0) {
      continue;
    }
    if (typeof record.reasoning_content !== 'string') {
      record.reasoning_content =
        typeof record.reasoning === 'string' ? record.reasoning : '';
    }
  }
}

function removeTextVerbosity(
  requestData: OpenAIChatCompletionsRequestData,
): void {
  const text = requestData.text;
  if (!isRecord(text) || !('verbosity' in text)) {
    return;
  }
  const nextText = { ...text };
  delete nextText.verbosity;
  if (Object.keys(nextText).length > 0) {
    requestData.text = nextText;
  } else {
    delete requestData.text;
  }
}

function pruneUndefinedValues(record: Record<string, unknown>): void {
  for (const key of Object.keys(record)) {
    if (record[key] === undefined) {
      delete record[key];
    }
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
